import React, { useState, useEffect, useCallback } from "react";  
import { useNavigate } from "react-router-dom";
import { Container } from "../components/ui/Container";
import { Stack } from "../components/ui/Stack";
import { Heading } from "../components/ui/Heading";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Spinner } from "../components/ui/Spinner";
import { Alert } from "../components/ui/Alert";
import { useApi } from "../context/ApiContext";
import { useAuth } from "../context/AuthContext";
import { getCurrencySymbol } from "../utils/countryCurrencyData";

interface Order {
 _id: string;
 orderNumber: string;
 status: "pending" | "processing" | "shipped" | "delivered" | "cancelled";
 total: number;
 currency: string;
 items: Array<{
  product: {
   _id: string;
   title: string;
  };
  quantity: number;
  price: number;
 }>;
 createdAt: string;
 shippingAddress?: {
  firstName: string;
  lastName: string;
  address1: string;
  city: string;
  country: string;
 };
}

export const Orders: React.FC = () => {
 const navigate = useNavigate();
 const { api } = useApi();
 const { user } = useAuth();
 const [orders, setOrders] = useState<Order[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);

 const fetchOrders = useCallback(async () => {
  try {
   setLoading(true);
   setError(null);
   const response = await api.get("/orders/my-orders");
   setOrders(response.data.orders || []);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to fetch orders");
  } finally {
   setLoading(false);
  }
 }, [api]);

 useEffect(() => {
  if (!user) {
   navigate("/login");
   return;
  }
  fetchOrders();
 }, [user, navigate, fetchOrders]);

 const getStatusColor = (status: string) => {
  switch (status) {
   case "delivered":
    return "bg-green-100 text-green-800";
   case "shipped":
    return "bg-blue-100 text-blue-800";
   case "processing":
    return "bg-yellow-100 text-yellow-800";
   case "pending":
    return "bg-gray-100 text-gray-800";
   case "cancelled":
    return "bg-red-100 text-red-800";
   default:
    return "bg-gray-100 text-gray-800";
  }
 };

 const formatPrice = (amount: number, currency: string): string => {
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${amount.toFixed(2)}`;
 };

 if (loading) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <Heading size="xl">My Orders</Heading>

    {error && <Alert variant="error">{error}</Alert>}

    {orders.length === 0 ? (
     <Card padding="lg">
      <Stack align="center" spacing="md">
       <Heading size="lg">No orders yet</Heading>
       <p className="text-gray-500">Start shopping to create your first order</p>
       <Button onClick={() => navigate("/products")}>
        Continue Shopping
       </Button>
      </Stack>
     </Card>
    ) : (
     <div className="space-y-4">
      {orders.map((order) => (
       <Card key={order._id} padding="lg">
        <div className="flex items-start justify-between mb-4">
         <div>
          <div className="font-semibold text-lg text-gray-900">
           Order #{order.orderNumber}
          </div>
          <div className="text-sm text-gray-500">
           {new Date(order.createdAt).toLocaleDateString()}
          </div>
         </div>
         <span
          className={`px-3 py-1 text-sm font-semibold ${getStatusColor(
           order.status
          )}`}
         >
          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
         </span>
        </div>

        <div className="mb-4 pb-4 border-b border-gray-200">
         <div className="text-sm text-gray-600 mb-2">
          {order.items.length} item{order.items.length !== 1 ? "s" : ""}
         </div>
         <div className="space-y-2">
          {order.items.map((item, idx) => (
           <div key={idx} className="flex justify-between text-sm">
            <div>
             <div className="text-gray-900">{item.product.title}</div>
             <div className="text-gray-500">Qty: {item.quantity}</div>
            </div>
            <div className="font-medium text-gray-900">
             {formatPrice(item.price * item.quantity, order.currency || "USD")}
            </div>
           </div>
          ))}
         </div>
        </div>

        <div className="flex items-center justify-between">
         <div>
          <div className="text-sm text-gray-500">Total</div>
          <div className="text-2xl font-bold text-gray-900">
           {formatPrice(order.total, order.currency || "USD")}
          </div>
         </div>
         <Button onClick={() => navigate(`/orders/${order._id}`)}>
          View Details
         </Button>
        </div>
       </Card>
      ))}
     </div>
    )}
   </Stack>
  </Container>
 );
};

export default Orders;
